import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WelcomeScreen from '../components/WelcomeScreen';
import useAdminSummary from '../hooks/useAdminSummary';
import useOnboardingProgress from '../hooks/useOnboardingProgress';
import LoadingView from '../../shared/components/LoadingView';

/**
 * WelcomePage
 * 
 * Dedicated page for the onboarding welcome screen.
 * Accessible at /admin/welcome
 */
export default function WelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const postOnboardingTarget = location.state?.from || '/admin/dashboard/';
  const { data: summaryData, loading: summaryLoading } = useAdminSummary();
  const {
    steps,
    completedCount,
    totalCount,
    isVisible,
    isLoading: onboardingLoading,
    dismissOnboarding,
  } = useOnboardingProgress(summaryData);

  const handleSkip = async () => {
    await dismissOnboarding();
    navigate(postOnboardingTarget);
  };

  const handleStepAction = (stepNumber) => {
    const step = steps?.find((s) => s.stepNumber === stepNumber);
    if (!step) return;
    const firstIncomplete = step.items?.find((item) => !item.completed);
    const path = firstIncomplete ? firstIncomplete.path : step.path;
    if (path) navigate(path);
  };

  // Show loading while data is being fetched
  if (summaryLoading || onboardingLoading) {
    return (
      <LoadingView
        symbolClass="bi bi-rocket-takeoff-fill"
        title="Loading onboarding"
        subtitle="Please wait..."
      />
    );
  }

  // Redirect if onboarding shouldn't be shown
  // (user isn't first user, or has completed/dismissed)
  if (!isVisible) {
    return (
      <div className="text-center mt-5">
        <h4>Onboarding Complete</h4>
        <p className="text-muted">
          You've already completed or dismissed the onboarding.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate(postOnboardingTarget)}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="py-4">
      <WelcomeScreen
        steps={steps}
        completedCount={completedCount}
        totalCount={totalCount}
        onSkip={handleSkip}
        onStepAction={handleStepAction}
      />
    </div>
  );
}
